import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import {
  PageContainer, PageHeader, Card, CardHeader,
  Table, Thead, Th, Td, TRow,
  Button, Badge, TabGroup, FormGroup, Input, Select, StatCard, EmptyState,
} from "../ui/index";

interface CountSession {
  id: string;
  name: string;
  status: string;
  started_by_name: string;
  completed_by_name: string | null;
  line_count: number;
  counted_count: number;
  total_adjustments: number;
  total_variance_oz: number;
  total_variance_dollars: number;
  created_at: string;
  completed_at: string | null;
}

interface CountLine {
  id: string;
  product_id: string;
  product_name: string;
  brand: string;
  category: string;
  case_configuration: string;
  vendor_name: string;
  oz_per_case: number;
  current_system_oz: number;
  expected_cases: number;
  expected_oz: number;
  counted_cases: number | null;
  counted_loose_units: number | null;
  counted_oz: number | null;
  variance_oz: number | null;
  cash_price: number;
  note: string | null;
  counted_at: string | null;
}

interface VarianceItem {
  id: string;
  name: string;
  category: string;
  case_configuration: string;
  oz_on_hand: number;
  oz_per_case: number;
  vendor_name: string;
  system_cases: number;
  stock_status: string;
  min_oz: number;
  max_oz: number;
}

const TABS = [
  { key: "count", label: "Active Count" },
  { key: "variance", label: "Variance Report" },
  { key: "history", label: "Count History" },
];

export default function InventoryCountPage() {
  const [tab, setTab] = useState("count");
  const [sessions, setSessions] = useState<CountSession[]>([]);
  const [activeSession, setActiveSession] = useState<(CountSession & { lines: CountLine[] }) | null>(null);
  const [variance, setVariance] = useState<{ products: VarianceItem[]; summary: Record<string, number> } | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [newVendor, setNewVendor] = useState("");
  const [vendors, setVendors] = useState<Array<{ id: string; name: string }>>([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Editable count values
  const [edits, setEdits] = useState<Record<string, { cases: string; loose: string; note: string }>>({});

  const loadHistory = useCallback(async () => {
    try {
      const res = await api.get("/inventory-counts/history");
      setSessions(res.data);
    } catch {}
  }, []);

  const loadVariance = useCallback(async () => {
    try {
      const res = await api.get("/inventory-counts/variance");
      setVariance(res.data);
    } catch {}
  }, []);

  const loadSession = useCallback(async (id: string) => {
    try {
      const res = await api.get(`/inventory-counts/sessions/${id}`);
      setActiveSession(res.data);
      // Pre-fill edits with any existing counts
      const e: Record<string, { cases: string; loose: string; note: string }> = {};
      for (const line of res.data.lines) {
        e[line.id] = {
          cases: line.counted_cases !== null ? String(line.counted_cases) : "",
          loose: line.counted_loose_units !== null ? String(line.counted_loose_units) : "",
          note: line.note || "",
        };
      }
      setEdits(e);
    } catch {
      setError("Failed to load count session");
    }
  }, []);

  useEffect(() => {
    if (tab === "history") loadHistory();
    if (tab === "variance") loadVariance();
    api.get("/vendors").then(r => setVendors(r.data)).catch(() => {});
  }, [tab, loadHistory, loadVariance]);

  // Check for in-progress session on load
  useEffect(() => {
    loadHistory().then(() => {
      // Auto-open if there's an in-progress session
    });
  }, [loadHistory]);

  const startCount = async () => {
    if (!newName.trim()) { setError("Name required"); return; }
    setError("");
    try {
      const body: Record<string, string> = { name: newName };
      if (newCategory) body.category = newCategory;
      if (newVendor) body.vendor_id = newVendor;
      const res = await api.post("/inventory-counts/sessions", body);
      setShowNewForm(false);
      setNewName("");
      setNewCategory("");
      setNewVendor("");
      await loadSession(res.data.id);
      setTab("count");
      setSuccess(`Count started: ${res.data.line_count} products to count`);
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Failed to start count");
    }
  };

  const updateEdit = (lineId: string, field: "cases" | "loose" | "note", value: string) => {
    setEdits(prev => ({
      ...prev,
      [lineId]: { ...prev[lineId], [field]: value },
    }));
  };

  const saveAllCounts = async () => {
    if (!activeSession) return;
    setSaving(true);
    setError("");

    const updates = [];
    for (const line of activeSession.lines) {
      const edit = edits[line.id];
      if (!edit || (edit.cases === "" && edit.loose === "")) continue;
      updates.push({
        line_id: line.id,
        counted_cases: Number(edit.cases) || 0,
        counted_loose_units: Number(edit.loose) || 0,
        note: edit.note || undefined,
      });
    }

    if (updates.length === 0) {
      setError("No counts entered");
      setSaving(false);
      return;
    }

    try {
      await api.post(`/inventory-counts/sessions/${activeSession.id}/bulk-update`, { updates });
      await loadSession(activeSession.id);
      setSuccess(`Saved ${updates.length} counts`);
    } catch {
      setError("Failed to save counts");
    } finally {
      setSaving(false);
    }
  };

  const finalizeCount = async () => {
    if (!activeSession) return;
    if (!window.confirm("Finalize this count? This will apply all variances to inventory. This cannot be undone.")) return;
    try {
      const res = await api.post(`/inventory-counts/sessions/${activeSession.id}/finalize`);
      setSuccess(`Count finalized: ${res.data.adjustments} adjustments, variance: $${res.data.total_variance_dollars}`);
      setActiveSession(null);
      loadHistory();
    } catch (err: unknown) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Failed to finalize");
    }
  };

  const filteredLines = activeSession?.lines.filter(l => {
    if (!search) return true;
    const s = search.toLowerCase();
    return l.product_name.toLowerCase().includes(s) || l.vendor_name?.toLowerCase().includes(s) || l.category.toLowerCase().includes(s);
  }) || [];

  const countedCount = activeSession?.lines.filter(l => edits[l.id]?.cases !== "" || l.counted_cases !== null).length || 0;
  const totalLines = activeSession?.lines.length || 0;

  const categories = ["domestic", "import", "craft", "seltzer", "rtd", "malternative", "cider", "na", "accessory"];

  return (
    <PageContainer wide>
      <PageHeader
        title="Inventory Counts"
        subtitle="Physical counts, variance tracking, and adjustments"
        action={
          <div className="flex gap-2">
            <Link to="/scan-count">
              <Button>Mobile Scan</Button>
            </Link>
            <Button onClick={() => setShowNewForm(true)}>Start New Count</Button>
          </div>
        }
      />

      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm font-medium" style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>
          {error} <button onClick={() => setError("")} className="float-right font-bold">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg text-sm font-medium" style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" }}>
          {success} <button onClick={() => setSuccess("")} className="float-right font-bold">&times;</button>
        </div>
      )}

      {/* New count modal */}
      {showNewForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-md p-6" style={{ background: "var(--rbw-bg-card)" }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>Start Inventory Count</h2>
            <div className="space-y-3">
              <FormGroup label="Count Name">
                <Input value={newName} onChange={setNewName} placeholder="e.g. Full Count May 13, Domestic Section, Origlio Products" />
              </FormGroup>
              <FormGroup label="Filter by Category (optional)">
                <Select value={newCategory} onChange={setNewCategory}>
                  <option value="">All categories</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </Select>
              </FormGroup>
              <FormGroup label="Filter by Vendor (optional)">
                <Select value={newVendor} onChange={setNewVendor}>
                  <option value="">All vendors</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </Select>
              </FormGroup>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowNewForm(false)}
                  className="flex-1 py-2 rounded-lg font-semibold text-sm" style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}>
                  Cancel
                </button>
                <button onClick={startCount}
                  className="flex-1 py-2 rounded-lg font-semibold text-sm" style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
                  Start Count
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <TabGroup tabs={TABS} active={tab} onChange={setTab} />

      {/* ACTIVE COUNT TAB */}
      {tab === "count" && (
        <>
          {!activeSession ? (
            <Card>
              <div className="p-8 text-center">
                <EmptyState message="No active count session" />
                <div className="mt-4 flex justify-center gap-3">
                  <Button onClick={() => setShowNewForm(true)}>Start New Count</Button>
                  <Link to="/scan-count"><Button>Mobile Scan & Count</Button></Link>
                </div>
                {sessions.filter(s => s.status === "in_progress").length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm mb-2" style={{ color: "var(--rbw-gray-dark)" }}>Or resume an in-progress count:</p>
                    {sessions.filter(s => s.status === "in_progress").map(s => (
                      <button key={s.id} onClick={() => loadSession(s.id)}
                        className="block mx-auto px-4 py-2 rounded-lg text-sm font-medium mb-1"
                        style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)", color: "var(--rbw-navy)" }}>
                        {s.name} ({s.counted_count}/{s.line_count} counted)
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          ) : (
            <>
              {/* Count header */}
              <div className="grid grid-cols-4 gap-4 mb-4">
                <StatCard label="Products" value={`${totalLines}`} />
                <StatCard label="Counted" value={`${countedCount}/${totalLines}`} variant={countedCount === totalLines ? "green" : "gold"} />
                <StatCard label="Session" value={activeSession.name} />
                <div className="flex items-end gap-2">
                  <Button onClick={saveAllCounts} disabled={saving}>
                    {saving ? "Saving..." : "Save All"}
                  </Button>
                  <Button onClick={finalizeCount}>Finalize</Button>
                </div>
              </div>

              {/* Search filter */}
              <div className="mb-3">
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Filter by product name, vendor, or category..."
                  className="w-full px-4 py-2 rounded-lg border text-sm"
                  style={{ borderColor: "var(--rbw-border)" }} />
              </div>

              {/* Count table */}
              <Card>
                <div style={{ maxHeight: "calc(100vh - 380px)", overflowY: "auto" }}>
                  <Table>
                    <Thead>
                      <tr>
                        <Th>Product</Th>
                        <Th>Config</Th>
                        <Th>Vendor</Th>
                        <Th align="right">System</Th>
                        <Th align="center">Cases</Th>
                        <Th align="center">Loose</Th>
                        <Th align="right">Variance</Th>
                        <Th>Note</Th>
                      </tr>
                    </Thead>
                    <tbody>
                      {filteredLines.map((line, idx) => {
                        const edit = edits[line.id] || { cases: "", loose: "", note: "" };
                        const systemCases = line.oz_per_case > 0 ? (line.current_system_oz / line.oz_per_case).toFixed(1) : "0";
                        const hasCount = edit.cases !== "" || line.counted_cases !== null;
                        const varianceCases = line.variance_oz !== null && line.oz_per_case > 0
                          ? (line.variance_oz / line.oz_per_case).toFixed(1) : null;

                        return (
                          <TRow key={line.id} index={idx}>
                            <Td>
                              <div className="font-medium text-sm" style={{ color: "var(--rbw-navy)" }}>{line.product_name}</div>
                              <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>{line.category}</div>
                            </Td>
                            <Td><span className="text-xs">{line.case_configuration}</span></Td>
                            <Td><span className="text-xs">{line.vendor_name}</span></Td>
                            <Td align="right">
                              <span className="text-sm font-medium">{systemCases}</span>
                            </Td>
                            <Td align="center">
                              <input type="number" inputMode="numeric" min="0"
                                value={edit.cases} onChange={e => updateEdit(line.id, "cases", e.target.value)}
                                className="w-16 px-2 py-1 rounded border text-center text-sm font-bold"
                                style={{
                                  borderColor: hasCount ? "var(--rbw-gold)" : "var(--rbw-border)",
                                  background: hasCount ? "#FFFBEB" : "white",
                                }}
                                placeholder="—" />
                            </Td>
                            <Td align="center">
                              <input type="number" inputMode="numeric" min="0"
                                value={edit.loose} onChange={e => updateEdit(line.id, "loose", e.target.value)}
                                className="w-16 px-2 py-1 rounded border text-center text-sm"
                                style={{ borderColor: "var(--rbw-border)" }}
                                placeholder="—" />
                            </Td>
                            <Td align="right">
                              {varianceCases !== null ? (
                                <span className="text-sm font-bold" style={{
                                  color: parseFloat(varianceCases) === 0 ? "var(--rbw-gray)" :
                                    parseFloat(varianceCases) > 0 ? "var(--rbw-success)" : "var(--rbw-danger)"
                                }}>
                                  {parseFloat(varianceCases) > 0 ? "+" : ""}{varianceCases}
                                </span>
                              ) : <span className="text-xs" style={{ color: "var(--rbw-gray)" }}>—</span>}
                            </Td>
                            <Td>
                              <input type="text" value={edit.note}
                                onChange={e => updateEdit(line.id, "note", e.target.value)}
                                className="w-24 px-1 py-1 rounded border text-xs"
                                style={{ borderColor: "var(--rbw-border)" }}
                                placeholder="note" />
                            </Td>
                          </TRow>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </>
          )}
        </>
      )}

      {/* VARIANCE REPORT TAB */}
      {tab === "variance" && variance && (
        <>
          <div className="grid grid-cols-4 gap-4 mb-4">
            <StatCard label="Negative Stock" value={String(variance.summary.negative)} variant={variance.summary.negative > 0 ? "danger" : "default"} />
            <StatCard label="Below Par" value={String(variance.summary.below_par)} variant={variance.summary.below_par > 0 ? "gold" : "default"} />
            <StatCard label="Above Par" value={String(variance.summary.above_par)} />
            <StatCard label="OK" value={String(variance.summary.ok)} variant="green" />
          </div>
          <Card>
            <Table>
              <Thead>
                <tr>
                  <Th>Product</Th>
                  <Th>Vendor</Th>
                  <Th align="right">Cases On Hand</Th>
                  <Th align="right">Min</Th>
                  <Th align="right">Max</Th>
                  <Th>Status</Th>
                </tr>
              </Thead>
              <tbody>
                {variance.products.map((p, idx) => (
                  <TRow key={p.id} index={idx}>
                    <Td>
                      <div className="font-medium" style={{ color: "var(--rbw-navy)" }}>{p.name}</div>
                      <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>{p.category} | {p.case_configuration}</div>
                    </Td>
                    <Td><span className="text-xs">{p.vendor_name}</span></Td>
                    <Td align="right">{Number(p.system_cases).toFixed(1)}</Td>
                    <Td align="right">{p.oz_per_case > 0 ? (p.min_oz / p.oz_per_case).toFixed(0) : "—"}</Td>
                    <Td align="right">{p.oz_per_case > 0 ? (p.max_oz / p.oz_per_case).toFixed(0) : "—"}</Td>
                    <Td>
                      <Badge variant={
                        p.stock_status === "negative" ? "danger" :
                        p.stock_status === "below_par" ? "gold" :
                        p.stock_status === "above_par" ? "blue" : "green"
                      }>
                        {p.stock_status.replace("_", " ")}
                      </Badge>
                    </Td>
                  </TRow>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      {/* HISTORY TAB */}
      {tab === "history" && (
        <Card>
          {sessions.length === 0 ? (
            <div className="p-8"><EmptyState message="No count sessions yet" /></div>
          ) : (
            <Table>
              <Thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Date</Th>
                  <Th>Started By</Th>
                  <Th align="right">Products</Th>
                  <Th align="right">Counted</Th>
                  <Th align="right">Adjustments</Th>
                  <Th align="right">Variance $</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </Thead>
              <tbody>
                {sessions.map((s, idx) => (
                  <TRow key={s.id} index={idx}>
                    <Td bold>{s.name}</Td>
                    <Td>{new Date(s.created_at).toLocaleDateString()}</Td>
                    <Td>{s.started_by_name}</Td>
                    <Td align="right">{s.line_count}</Td>
                    <Td align="right">{s.counted_count}</Td>
                    <Td align="right">{s.total_adjustments}</Td>
                    <Td align="right">
                      <span style={{ color: Number(s.total_variance_dollars) < 0 ? "var(--rbw-danger)" : "var(--rbw-navy)" }}>
                        ${Number(s.total_variance_dollars).toFixed(2)}
                      </span>
                    </Td>
                    <Td>
                      <Badge variant={s.status === "completed" ? "green" : s.status === "in_progress" ? "gold" : "default"}>
                        {s.status.replace("_", " ")}
                      </Badge>
                    </Td>
                    <Td>
                      {s.status === "in_progress" && (
                        <button onClick={() => { loadSession(s.id); setTab("count"); }}
                          className="text-xs font-semibold px-2 py-1 rounded"
                          style={{ color: "var(--rbw-navy)" }}>
                          Resume
                        </button>
                      )}
                    </Td>
                  </TRow>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}
    </PageContainer>
  );
}
